import React, { useState } from 'react';
import { getApiUrl, getFullApiUrl } from '../config';
import { saveToken, saveUser, savePagesPermissions, getAuthHeaders } from '../utils/auth';

const Login = ({ onLogin, onBackToLanding }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (!formData.email || !formData.password) {
      setError('Email dan password harus diisi');
      setLoading(false);
      return;
    }

    try {
      // Call login API
      const response = await fetch(getApiUrl('api/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle error response
        setError(data.detail || 'Login gagal. Silakan cek email dan password Anda.');
        setLoading(false);
        return;
      }

      // Login successful - save token and user data
      if (data.access_token && data.user) {
        saveToken(data.access_token);
        // Save initial user data from login response
        saveUser(data.user);

        // Fetch complete user data from API to get latest info including is_active
        let completeUserData = data.user; // Use login response as fallback
        try {
          const meResponse = await fetch(getFullApiUrl('ME'), {
            method: 'GET',
            headers: getAuthHeaders()
          });

          if (meResponse.ok) {
            completeUserData = await meResponse.json();
            // Save complete user data to localStorage
            saveUser(completeUserData);
          }
        } catch (err) {
          console.error('Error fetching user data from /me endpoint:', err);
          // Continue with login response data if /me fails
        }

        // Fetch user's accessible pages with permissions
        let permissions = [];
        try {
          const pagesResponse = await fetch(getFullApiUrl('MY_PAGES'), {
            method: 'GET',
            headers: getAuthHeaders()
          });

          if (pagesResponse.ok) {
            const pagesData = await pagesResponse.json();
            permissions = pagesData.pages || [];
            savePagesPermissions(permissions);
          }
        } catch (err) {
          console.error('Error fetching pages permissions:', err);
          // Continue login even if pages fetch fails
        }

        // Call onLogin callback with complete user data (including is_active) and permissions
        onLogin({
          ...completeUserData, // Use complete user data from /me endpoint
          permissions: permissions
        });

        setLoading(false);
      } else {
        setError('Response tidak valid dari server');
        setLoading(false);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Terjadi kesalahan saat login. Silakan coba lagi.');
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-blue-100 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden transition-colors duration-300">
      {/* Simple Silhouette Background - Minimalist Ambience with Gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 1920 1080" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            {/* Gradient definitions - More visible */}
            <radialGradient id="gradient1" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#0070f3" stopOpacity="0.25" />
              <stop offset="40%" stopColor="#0070f3" stopOpacity="0.12" />
              <stop offset="70%" stopColor="#0070f3" stopOpacity="0.05" />
              <stop offset="100%" stopColor="#0070f3" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="gradient2" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#0070f3" stopOpacity="0.20" />
              <stop offset="40%" stopColor="#0070f3" stopOpacity="0.10" />
              <stop offset="70%" stopColor="#0070f3" stopOpacity="0.04" />
              <stop offset="100%" stopColor="#0070f3" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="gradient3" cx="50%" cy="50%">
              <stop offset="0%" stopColor="#0070f3" stopOpacity="0.18" />
              <stop offset="40%" stopColor="#0070f3" stopOpacity="0.08" />
              <stop offset="70%" stopColor="#0070f3" stopOpacity="0.03" />
              <stop offset="100%" stopColor="#0070f3" stopOpacity="0" />
            </radialGradient>
          </defs>
          {/* Subtle gradient circles for ambience */}
          <circle cx="200" cy="200" r="150" fill="url(#gradient1)" />
          <circle cx="1720" cy="880" r="200" fill="url(#gradient2)" />
          <circle cx="960" cy="540" r="300" fill="url(#gradient3)" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-10">
          <div className="inline-block mb-6">
            <img
              src={`${process.env.PUBLIC_URL}/lg_tabulara.png`}
              alt="Tabulara Logo"
              className="h-12 mx-auto object-contain dark:brightness-0 dark:invert transition-all duration-300"
            />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300">Selamat Datang</h1>
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-2 transition-colors duration-300">Masuk ke akun Anda untuk melanjutkan</p>

          {/* Back Button */}
          {onBackToLanding && (
            <div className="flex flex-col items-center">
              <span className="text-gray-400 dark:text-gray-500 text-sm mb-2">atau</span>
              <button
                onClick={onBackToLanding}
                className="flex items-center justify-center mx-auto text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors group text-sm"
              >
                <i className="fa-solid fa-arrow-left mr-2 group-hover:-translate-x-1 transition-transform duration-200"></i>
                <span>Kembali ke Beranda</span>
              </button>
            </div>
          )}
        </div>

        {/* Login Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 p-8 transition-colors duration-300">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
                <i className="fa-solid fa-circle-exclamation mr-2"></i>
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fa-solid fa-envelope text-gray-400 dark:text-gray-500"></i>
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all duration-200"
                  placeholder="nama@email.com"
                  required
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 transition-colors duration-300">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="fa-solid fa-lock text-gray-400 dark:text-gray-500"></i>
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full pl-10 pr-10 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all duration-200"
                  placeholder="Masukkan password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg text-sm font-semibold text-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200 ${loading
                  ? 'bg-primary-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700'
                }`}
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Memproses...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-sign-in-alt mr-2"></i>
                  Masuk
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500 dark:text-gray-400 transition-colors duration-300">
          <p>© {new Date().getFullYear()} Tabulara. All rights reserved.</p>
          <p className="mt-2">
            by{' '}
            <a
              href="https://dssolution.id/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline transition-colors duration-200"
            >
              DS Solution
            </a>
          </p>
        </div>
      </div>

    </div>
  );
};

export default Login;

